import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaApple, FaAndroid, FaGlobe, FaImage, FaUpload } from 'react-icons/fa';
import { authFetch, getAuthHeaders } from './authHelper';

const emptyProject = {
  name: '', category: '', description: '', technologies: [], image_url: '', link: '', ios_link: '', android_link: '', display_order: 0, active: 1,
};

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const [techInput, setTechInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchProjects = () => {
    authFetch('/api/admin/projects.php')
      .then((res) => res.json())
      .then((data) => { setProjects(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEdit = editing !== null && editing !== 'new';
    const url = isEdit ? `/api/admin/projects.php?id=${editing}` : '/api/admin/projects.php';
    const method = isEdit ? 'PUT' : 'POST';

    await authFetch(url, {
      method,
      headers: getAuthHeaders(),
      body: JSON.stringify(form),
    });

    setEditing(null);
    setForm(emptyProject);
    setTechInput('');
    fetchProjects();
  };

  const handleEdit = (project) => {
    setEditing(project.id);
    const techs = typeof project.technologies === 'string' ? JSON.parse(project.technologies) : (project.technologies || []);
    setForm({ ...project, technologies: techs });
    setTechInput(techs.join(', '));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    await authFetch(`/api/admin/projects.php?id=${id}`, { method: 'DELETE' });
    fetchProjects();
  };

  const handleTechChange = (value) => {
    setTechInput(value);
    setForm({ ...form, technologies: value.split(',').map((t) => t.trim()).filter(Boolean) });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const token = localStorage.getItem('admin_token');
      const res = await fetch('/api/admin/upload-image.php', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setForm((prev) => ({ ...prev, image_url: data.url }));
      } else {
        alert(data.error || "Erreur lors de l'upload");
      }
    } catch {
      alert("Erreur lors de l'upload");
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (loading) return <div className="admin-loading">Chargement...</div>;

  return (
    <div>
      <div className="admin-page-header">
        <h2 className="admin-page-title">Projets réalisés</h2>
        {editing === null && (
          <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
            <FaPlus /> Ajouter
          </button>
        )}
      </div>

      {editing !== null && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <h3>{editing === 'new' ? 'Nouveau projet' : 'Modifier le projet'}</h3>
          <div className="admin-form-grid">
            <div className="admin-field">
              <label>Nom du projet *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="admin-field">
              <label>Catégorie *</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required>
                <option value="">Sélectionner</option>
                <option value="Application Mobile">Application Mobile</option>
                <option value="Application Web">Application Web</option>
                <option value="Site Vitrine">Site Vitrine</option>
                <option value="Site E-commerce">Site E-commerce</option>
              </select>
            </div>
            <div className="admin-field full-width">
              <label>Description *</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows="3" required />
            </div>
            <div className="admin-field">
              <label>Technologies (séparées par des virgules)</label>
              <input type="text" value={techInput} onChange={(e) => handleTechChange(e.target.value)} placeholder="React, Node.js, Firebase" />
            </div>
            <div className="admin-field">
              <label><FaGlobe /> Lien web</label>
              <input type="url" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." />
            </div>
            <div className="admin-field">
              <label><FaApple /> Lien App Store (iOS)</label>
              <input type="url" value={form.ios_link || ''} onChange={(e) => setForm({ ...form, ios_link: e.target.value })} placeholder="https://apps.apple.com/..." />
            </div>
            <div className="admin-field">
              <label><FaAndroid /> Lien Google Play (Android)</label>
              <input type="url" value={form.android_link || ''} onChange={(e) => setForm({ ...form, android_link: e.target.value })} placeholder="https://play.google.com/..." />
            </div>
            <div className="admin-field full-width">
              <label><FaImage /> Image du projet</label>
              <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageUpload} style={{ display: 'none' }} />
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <button type="button" className="admin-dropzone" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  style={{ flex: 1, justifyContent: 'center' }}>
                  <FaUpload /> {uploading ? 'Upload en cours...' : 'Choisir une image'}
                </button>
                {form.image_url && (
                  <div style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--border)', flexShrink: 0 }}>
                    <img src={form.image_url} alt="Aperçu" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button type="button" onClick={() => setForm({ ...form, image_url: '' })}
                      style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(0,0,0,0.7)', border: 'none', color: '#ff6b6b', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>
                      <FaTimes />
                    </button>
                  </div>
                )}
              </div>
              {form.image_url && <input type="text" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="ou saisir une URL" style={{ marginTop: '8px' }} />}
            </div>
            <div className="admin-field">
              <label>Ordre d'affichage</label>
              <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <div className="admin-form-actions">
            <button type="submit" className="btn btn-primary btn-sm"><FaSave /> Enregistrer</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setEditing(null); setForm(emptyProject); }}>
              <FaTimes /> Annuler
            </button>
          </div>
        </form>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Nom</th>
              <th>Catégorie</th>
              <th>Technologies</th>
              <th>Liens</th>
              <th>Actif</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 ? (
              <tr><td colSpan="7" className="admin-empty">Aucun projet</td></tr>
            ) : (
              projects.map((p) => {
                const techs = typeof p.technologies === 'string' ? JSON.parse(p.technologies) : (p.technologies || []);
                return (
                  <tr key={p.id}>
                    <td>
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px' }} />
                      ) : (
                        <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: 'rgba(108,92,231,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-gray)' }}><FaImage /></div>
                      )}
                    </td>
                    <td><strong>{p.name}</strong></td>
                    <td>{p.category}</td>
                    <td>{techs.join(', ')}</td>
                    <td style={{ fontSize: '1.1rem', display: 'flex', gap: '6px' }}>
                      {p.link && <a href={p.link} target="_blank" rel="noopener noreferrer" title="Web"><FaGlobe /></a>}
                      {p.ios_link && <a href={p.ios_link} target="_blank" rel="noopener noreferrer" title="iOS" style={{ color: '#a2aaad' }}><FaApple /></a>}
                      {p.android_link && <a href={p.android_link} target="_blank" rel="noopener noreferrer" title="Android" style={{ color: '#3ddc84' }}><FaAndroid /></a>}
                      {!p.link && !p.ios_link && !p.android_link && <span style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>—</span>}
                    </td>
                    <td>{p.active ? 'Oui' : 'Non'}</td>
                    <td className="admin-actions">
                      <button onClick={() => handleEdit(p)} title="Modifier"><FaEdit /></button>
                      <button onClick={() => handleDelete(p.id)} title="Supprimer" className="danger"><FaTrash /></button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminProjects;
